import { useState, useEffect, useContext } from "react";
import { BadgeQuestionMark, X } from "lucide-react";
import { createPortal } from "react-dom";
import { usePagination, PaginationItemType, cn } from "@heroui/react";
import { getUrl } from "aws-amplify/storage";
import { BackendContext } from "../Context/BackendContext";

export const ChevronIcon = (props) => (
  <svg aria-hidden="true" fill="none" height="1em" width="1em" viewBox="0 0 24 24" {...props}>
    <path d="M15.5 19l-7-7 7-7" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" />
  </svg>
);

const TUTORIAL_SEEN_KEY = "artparcel:tutorialSeen:v1";

// 👇 Define your tutorial steps here
const TUTORIAL_STEPS = [
  {
    id: 1,
    title: "Welcome to ArtParcel",
    image: "general/tutorial/Tutorial1.png",
    description: "A collaborative pixel canvas. In Artparcel, your artwork is truly yours, and no one can modify it.",
    bullets: [
      "Claim pixels and draw pixel art.",
      "Spend Pixel Drops to extend ownership.",
      //"Look at other users' art and like your favorites.",
    ],
  },
  {
    id: 2,
    title: "Claim & Extend Artworks",
    image: "general/tutorial/Tutorial2.png",
    description: "In Artparcel, your Artwork is truly yours.",
    bullets: [
      "Any pixel you buy, no one else can change it.",
      "No griefing, griefing is impossible with ownership",
      "Any expired pixels are free for grabs by anyone.",
    ],
  },
];

const TutorialModal = () => {
  const [showModal, setShowModal] = useState(false);

  const [imgUrl, setImgUrl] = useState(null);
  const { userProfile } = useContext(BackendContext);

  // Pagination state using HeroUI’s hook
  const totalPages = TUTORIAL_STEPS.length;
  const { activePage, range, setPage, onNext, onPrevious } = usePagination({
    total: totalPages,
    showControls: true,
    siblings: 3,
    boundaries: 0,
  });

  const step = TUTORIAL_STEPS[(activePage - 1) % totalPages];

  // --- autostart only if not seen on this device ---
  useEffect(() => {
    const seen = localStorage.getItem(TUTORIAL_SEEN_KEY) === "1";

    const isLoggedIn = !!userProfile;

    if (!seen || isLoggedIn) {
      setShowModal(true);
    }

    // (optional) handy for dev: window.resetTutorialSeen?.()
    window.resetTutorialSeen = () => localStorage.removeItem(TUTORIAL_SEEN_KEY);
  }, []);

  //Resolve S3 key -> signed URL (or pass though for local / images)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        if (!step.image) { if (alive) setImgUrl(null); return; }
        // If it's a local/static asset path, just use it
        if (step.image.startsWith("/")) {
          if (alive) setImgUrl(step.image);
          return;
        }
        // Otherwise treat it as an S3 key (e.g., "general/Tutorial1.png")
        const { url } = await getUrl({
          path: step.image,
        })
        if (alive) setImgUrl(url.toString());
      } catch (e) {
        console.error("Error getting image URL for tutorial step:", e);
        if (alive) setImgUrl(null); // Fallback to null on error
      }
    })();
    return () => { alive = false; };
  }, [step?.image]);

  // Keyboard navigation
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowModal(false);
      if (e.key === "ArrowRight") onNext();
      if (e.key === "ArrowLeft") onPrevious();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, onNext, onPrevious]);

  // --- mark as seen + close ---
  const finishTutorial = () => {
    localStorage.setItem(TUTORIAL_SEEN_KEY, "1");
    setShowModal(false);
  };

  return (
    <div>
      {/* Trigger icon */}
      <div className="cursor-pointer relative" onClick={() => setShowModal(true)}>
        <BadgeQuestionMark  className="w-6 h-6 text-gray-500" />
      </div>

      {/* Modal */}
      {showModal &&
        createPortal(
          <div className="fixed inset-0 z-[1000] bg-black/70">
            <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="
                bg-slate-900 text-white rounded-xl w-full max-w-2xl
                /* keep the whole card inside viewport with some breathing room */
                max-h-[min(90vh,100dvh)]
                /* column layout so header/body/footer play nice */
                flex flex-col
                /* containment */
                overflow-hidden shadow-lg border border-slate-700
              "
              role="dialog"
              aria-modal="true"
            >
              {/* Header */}
              <div className="shrink-0 flex items-center justify-between p-4 border-b border-slate-800">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400">
                    Tutorial • Step {activePage} of {totalPages}
                  </div>
                  <h2 className="text-xl font-bold">{step.title}</h2>
                </div>
                <button
                  className="p-2 rounded hover:bg-slate-800"
                  aria-label="Close tutorial"
                  onClick={() => setShowModal(false)}
                >
                  <X />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 min-h-0 p-6 space-y-4 overflow-y-auto">
                {/* Big picture */}
                <div
                  className="
                    w-full aspect-video bg-slate-800 rounded-lg
                    flex items-center justify-center overflow-hidden border border-slate-700
                    /* prevent the image area from growing too tall on short screens */
                    max-h-[45vh]
                  "
                >
                  <img
                    src={imgUrl}
                    alt={step.title}
                    className="w-full h-full object-contain"
                    draggable="false"
                  />
                </div>

                {/* Description */}
                <p className="text-slate-300">{step.description}</p>

                {/* Bullet points */}
                <ul className="list-disc pl-6 space-y-1 text-slate-200">
                  {step.bullets.map((b, i) => (
                    <li key={i}>{b}</li>
                  ))}
                </ul>
              </div>

              {/* Footer: prev / dots / next */}
              <div className="shrink-0 px-4 py-3 border-t border-slate-800 flex items-center justify-between">
                <button
                  className={cn(
                    "flex items-center gap-1 px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40"
                  )}
                  onClick={onPrevious}
                  disabled={activePage === 1}
                >
                  <ChevronIcon />
                  <span className="text-sm">Prev</span>
                </button>

                {/* Dots only (no numbers) */}
                <ul className="flex items-center gap-2">
                  {range.map((page, idx) => {
                    if (
                      page === PaginationItemType.NEXT ||
                      page === PaginationItemType.PREV
                    ) {
                      return null; // we render our own Prev/Next buttons
                    }
                    if (page === PaginationItemType.DOTS) {
                      // If the hook inserts DOTS (shouldn’t with siblings:0/boundaries:0 but keep safe)
                      return (
                        <li key={`dots-${idx}`} className="w-2 h-2 rounded-full bg-slate-600 opacity-60" />
                      );
                    }
                    // Numeric page → render as a dot
                    return (
                      <li key={`dot-${page}`}>
                        <button
                          aria-label={`Go to step ${page}`}
                          className={cn(
                            "w-2.5 h-2.5 rounded-full transition-colors",
                            activePage === page ? "bg-purple-500" : "bg-slate-600 hover:bg-slate-500"
                          )}
                          onClick={() => setPage(page)}
                        />
                      </li>
                    );
                  })}
                </ul>

                {activePage === totalPages ? (
                  <button
                    className="flex items-center gap-1 px-3 py-1.5 rounded bg-purple-600 hover:bg-purple-500"
                    onClick={finishTutorial}
                  >
                    <span className="text-sm font-semibold">Finish tutorial</span>
                    <ChevronIcon className="rotate-180" />
                  </button>
                ) : (
                  <button
                    className={cn(
                      "flex items-center gap-1 px-3 py-1.5 rounded bg-slate-800 hover:bg-slate-700 disabled:opacity-40"
                    )}
                    onClick={onNext}
                    disabled={activePage === totalPages}
                  >
                    <span className="text-sm">Next</span>
                    <ChevronIcon className="rotate-180" />
                  </button>
                )}
              </div>
            </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default TutorialModal;
